import styled, { css, keyframes } from 'styled-components';
import theme from '../../theme';

/* ─── Floating Action Button ─── */

export const FloatingButton = styled.button`
    position: fixed;
    bottom: 24px;
    right: 24px;
    z-index: 1050;
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: none;
    background: ${theme.colors.brand.darkBlue};
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;

    &:hover {
        transform: scale(1.08);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
        background: ${theme.colors.brand.lightBlue};
    }

    &:active {
        transform: scale(0.96);
    }

    @media print {
        display: none;
    }
`;

export const FloatingButtonIcon = styled.span`
    font-size: 24px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
`;

/* ─── Drawer Overlay ─── */

const slideIn = keyframes`
    from { transform: translateX(100%); }
    to   { transform: translateX(0); }
`;

const slideOut = keyframes`
    from { transform: translateX(0); }
    to   { transform: translateX(100%); }
`;

export const DrawerOverlay = styled.div<{ $isClosing: boolean }>`
    position: fixed;
    inset: 0;
    z-index: 1040;
    background: rgba(0, 0, 0, 0.15);
    animation: ${({ $isClosing }) =>
        $isClosing
            ? css`
                  ${slideOut} 0.25s ease forwards
              `
            : 'none'};

    @media print {
        display: none;
    }
`;

export const DrawerPanel = styled.aside<{ $isClosing: boolean }>`
    position: fixed;
    top: 0;
    right: 0;
    z-index: 1045;
    width: 420px;
    max-width: 100vw;
    height: 100vh;
    background: #fff;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
    display: flex;
    flex-direction: column;
    animation: ${({ $isClosing }) =>
        $isClosing
            ? css`
                  ${slideOut} 0.25s ease forwards
              `
            : css`
                  ${slideIn} 0.25s ease
              `};

    @media print {
        display: none;
    }
`;

/* ─── Drawer Header ─── */

export const DrawerHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid ${theme.colors.shadow.ddd};
    background: ${theme.colors.brand.darkBlue};
    color: #fff;
    flex-shrink: 0;
`;

export const DrawerTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
`;

export const DrawerHeaderActions = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const HeaderIconButton = styled.button`
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.75);
    cursor: pointer;
    padding: 4px 6px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    font-size: 16px;
    transition: color 0.15s, background 0.15s;

    &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.15);
    }
`;

/* ─── Message Area ─── */

export const MessageArea = styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

export const WelcomeContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    text-align: center;
    padding: 24px 16px;
    color: ${theme.colors.text.lightGrey};
`;

export const WelcomeIcon = styled.div`
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.6;
`;

export const WelcomeTitle = styled.h3`
    margin: 0 0 8px;
    font-size: 18px;
    font-weight: 600;
    color: ${theme.colors.text.primary};
`;

export const WelcomeSubtitle = styled.p`
    margin: 0 0 20px;
    font-size: 13px;
    line-height: 1.5;
    max-width: 280px;
`;

/* ─── Suggestions ─── */

export const SuggestionsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: center;
`;

export const SuggestionChip = styled.button`
    background: ${theme.colors.background.fafafa};
    border: 1px solid ${theme.colors.shadow.ddd};
    border-radius: 20px;
    padding: 6px 14px;
    font-size: 12px;
    color: ${theme.colors.text.primary};
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    white-space: nowrap;

    &:hover {
        border-color: ${theme.colors.brand.lightBlue};
        background: ${theme.colors.background.selected};
    }
`;

/* ─── Chat Bubble ─── */

export const BubbleRow = styled.div<{ $isUser: boolean }>`
    display: flex;
    justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
`;

export const Bubble = styled.div<{ $isUser: boolean }>`
    max-width: 85%;
    padding: 10px 14px;
    border-radius: ${({ $isUser }) => ($isUser ? '16px 16px 4px 16px' : '16px 16px 16px 4px')};
    background: ${({ $isUser }) => ($isUser ? theme.colors.brand.darkBlue : theme.colors.background.fafafa)};
    color: ${({ $isUser }) => ($isUser ? '#fff' : theme.colors.text.primary)};
    font-size: 13px;
    line-height: 1.55;
    word-break: break-word;

    /* Markdown content styling */
    p {
        margin: 0 0 8px;
        &:last-child {
            margin-bottom: 0;
        }
    }
    ul,
    ol {
        margin: 4px 0 8px 16px;
        padding: 0;
    }
    li {
        margin-bottom: 2px;
    }
    code {
        background: ${({ $isUser }) => ($isUser ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.06)')};
        padding: 1px 4px;
        border-radius: 3px;
        font-size: 12px;
    }
    pre {
        background: ${({ $isUser }) => ($isUser ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.04)')};
        padding: 8px 10px;
        border-radius: 6px;
        overflow-x: auto;
        code {
            background: none;
            padding: 0;
        }
    }
    strong {
        font-weight: 600;
    }
    blockquote {
        margin: 4px 0;
        padding: 4px 10px;
        border-left: 3px solid ${({ $isUser }) => ($isUser ? 'rgba(255,255,255,0.4)' : theme.colors.brand.lightBlue)};
        opacity: 0.9;
    }
    a {
        color: ${({ $isUser }) => ($isUser ? '#b9dcff' : theme.colors.text.blueLink)};
    }
`;

export const BubbleTimestamp = styled.span<{ $isUser: boolean }>`
    display: block;
    font-size: 10px;
    margin-top: 4px;
    opacity: 0.55;
    text-align: ${({ $isUser }) => ($isUser ? 'right' : 'left')};
`;

/* ─── Typing Indicator ─── */

const bounce = keyframes`
    0%, 60%, 100% { transform: translateY(0); }
    30% { transform: translateY(-4px); }
`;

export const TypingDots = styled.div`
    display: flex;
    gap: 4px;
    padding: 4px 0;

    span {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${theme.colors.text.lightGrey};
        animation: ${bounce} 1.2s infinite;

        &:nth-child(2) {
            animation-delay: 0.15s;
        }
        &:nth-child(3) {
            animation-delay: 0.3s;
        }
    }
`;

/* ─── Input Area ─── */

export const InputArea = styled.form`
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid ${theme.colors.shadow.ddd};
    background: #fff;
    flex-shrink: 0;
`;

export const ChatInput = styled.textarea`
    flex: 1;
    resize: none;
    border: 1px solid ${theme.colors.shadow.ddd};
    border-radius: 12px;
    padding: 10px 14px;
    font-size: 13px;
    font-family: inherit;
    line-height: 1.4;
    max-height: 120px;
    outline: none;
    transition: border-color 0.15s;

    &:focus {
        border-color: ${theme.colors.brand.lightBlue};
    }

    &::placeholder {
        color: ${theme.colors.text.italicGrey};
    }
`;

export const SendButton = styled.button<{ $active: boolean }>`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: ${({ $active }) => ($active ? theme.colors.brand.darkBlue : theme.colors.shadow.ddd)};
    color: ${({ $active }) => ($active ? '#fff' : theme.colors.text.lightGrey)};
    cursor: ${({ $active }) => ($active ? 'pointer' : 'default')};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;

    &:hover {
        background: ${({ $active }) => ($active ? theme.colors.brand.lightBlue : theme.colors.shadow.ddd)};
    }
`;

export const StopButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: none;
    background: ${theme.colors.status.errorRed};
    color: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s;

    &:hover {
        background: ${theme.colors.text.error};
    }
`;

/* ─── Preview badge ─── */

export const PreviewBadge = styled.div`
    text-align: center;
    padding: 4px 0 0;
    font-size: 10px;
    color: ${theme.colors.text.italicGrey};
    flex-shrink: 0;
`;
